import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { AdminAuthProvider } from './context/AdminAuthContext';
import PrivateRoute from './components/PrivateRoute';
import AdminPrivateRoute from './components/AdminPrivateRoute';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import About from './pages/About';
import Contact from './pages/Contact';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Profiles from './pages/Profiles';
import ProfileWizard from './pages/ProfileWizard';
import Stories from './pages/Stories';
import StoryDetail from './pages/StoryDetail';
import PWADesktop from './pages/PWADesktop';
import StoriesEmptyState from './pages/StoriesEmptyState';
import HelpCenter from './pages/HelpCenter';
import Roadmap from './pages/Roadmap';
import Feedback from './pages/Feedback';
import Pricing from './pages/Pricing';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminProfiles from './pages/admin/AdminProfiles';
import AdminStories from './pages/admin/AdminStories';
import AdminProfile from './pages/admin/AdminProfile';
import AdminApiKeys from './pages/admin/AdminApiKeys';
import AdminFeedbacks from './pages/admin/AdminFeedbacks';
import Account from './pages/Account';
import AdminReviews from './pages/admin/AdminReviews';
import AdminAdmins from './pages/admin/AdminAdmins';
import AdminPlans from './pages/admin/AdminPlans';
import AdminSubscriptions from './pages/admin/AdminSubscriptions';
import AdminContentThemes from './pages/admin/AdminContentThemes';
import AdminContentInterests from './pages/admin/AdminContentInterests';
import AdminContentTones from './pages/admin/AdminContentTones';
import AdminContentLanguages from './pages/admin/AdminContentLanguages';
import AdminContentPrivacy from './pages/admin/AdminContentPrivacy';
import AdminContentTerms from './pages/admin/AdminContentTerms';
import AdminContentHelp from './pages/admin/AdminContentHelp';
import TrackingScripts from './components/TrackingScripts';
import BackToTopButton from './components/BackToTopButton';
import ScrollToTop from './components/ScrollToTop';
import SeoHead from './components/SeoHead';
import './App.css';

function App() {
  return (
    <AuthProvider>
      <AdminAuthProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <div className="App">
            <ScrollToTop />
            <SeoHead />
            <TrackingScripts />
            <Toaster position="top-right" />
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/roadmap" element={<Roadmap />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route
                path="/account"
                element={
                  <PrivateRoute>
                    <Account />
                  </PrivateRoute>
                }
              />
              
              {/* User Routes */}
              <Route
                path="/profiles"
                element={
                  <PrivateRoute>
                    <Profiles />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profiles/create"
                element={<ProfileWizard />}
              />
              <Route
                path="/profiles/:id"
                element={
                  <PrivateRoute>
                    <ProfileWizard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/stories"
                element={
                  <PrivateRoute>
                    <Stories />
                  </PrivateRoute>
                }
              />
              <Route
                path="/stories/empty-state"
                element={
                  <PrivateRoute>
                    <StoriesEmptyState />
                  </PrivateRoute>
                }
              />
              <Route
                path="/stories/:id"
                element={
                  <PrivateRoute>
                    <StoryDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/experience/pwa"
                element={
                  <PrivateRoute>
                    <PWADesktop />
                  </PrivateRoute>
                }
              />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin"
                element={
                  <AdminPrivateRoute>
                    <AdminDashboard />
                  </AdminPrivateRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminPrivateRoute>
                    <AdminUsers />
                  </AdminPrivateRoute>
                }
              />
              <Route
                path="/admin/profiles"
                element={
                  <AdminPrivateRoute>
                    <AdminProfiles />
                  </AdminPrivateRoute>
                }
              />
              <Route
                path="/admin/stories"
                element={
                  <AdminPrivateRoute>
                    <AdminStories />
                  </AdminPrivateRoute>
                }
              />
              <Route
                path="/admin/profile"
                element={
                  <AdminPrivateRoute>
                    <AdminProfile />
                  </AdminPrivateRoute>
                }
              />
              <Route
                path="/admin/api-keys"
                element={
                  <AdminPrivateRoute>
                    <AdminApiKeys />
                  </AdminPrivateRoute>
                }
              />
              <Route
                path="/admin/feedbacks"
                element={
                  <AdminPrivateRoute>
                    <AdminFeedbacks />
                  </AdminPrivateRoute>
                }
              />
              <Route
                path="/admin/reviews"
                element={
                  <AdminPrivateRoute>
                    <AdminReviews />
                  </AdminPrivateRoute>
                }
              />
              <Route
                path="/admin/admins"
                element={
                  <AdminPrivateRoute>
                    <AdminAdmins />
                  </AdminPrivateRoute>
                }
              />
              <Route
                path="/admin/plans"
                element={
                  <AdminPrivateRoute>
                    <AdminPlans />
                  </AdminPrivateRoute>
                }
              />
              <Route
                path="/admin/subscriptions"
                element={
                  <AdminPrivateRoute>
                    <AdminSubscriptions />
                  </AdminPrivateRoute>
                }
              />
              <Route
                path="/admin/content/themes"
                element={
                  <AdminPrivateRoute>
                    <AdminContentThemes />
                  </AdminPrivateRoute>
                }
              />
              <Route
                path="/admin/content/interests"
                element={
                  <AdminPrivateRoute>
                    <AdminContentInterests />
                  </AdminPrivateRoute>
                }
              />
              <Route
                path="/admin/content/tones"
                element={
                  <AdminPrivateRoute>
                    <AdminContentTones />
                  </AdminPrivateRoute>
                }
              />
              <Route
                path="/admin/content/languages"
                element={
                  <AdminPrivateRoute>
                    <AdminContentLanguages />
                  </AdminPrivateRoute>
                }
              />
              <Route
                path="/admin/content/privacy"
                element={
                  <AdminPrivateRoute>
                    <AdminContentPrivacy />
                  </AdminPrivateRoute>
                }
              />
              <Route
                path="/admin/content/terms"
                element={
                  <AdminPrivateRoute>
                    <AdminContentTerms />
                  </AdminPrivateRoute>
                }
              />
              <Route
                path="/admin/content/help"
                element={
                  <AdminPrivateRoute>
                    <AdminContentHelp />
                  </AdminPrivateRoute>
                }
              />
              
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <BackToTopButton />
          </div>
        </Router>
      </AdminAuthProvider>
    </AuthProvider>
  );
}

export default App;

